<template>

    <v-card flat>

        <v-card-text>

            <v-alert v-show="!reCaptchaAvailable" type="warning" icon="mdi-alert" class="body-2 mt-0 mb-2">
                reCaptcha is not configured in the Panel Settings. We strongly recommend you configure and enable it.
            </v-alert>

            <!-- SURVEY OPTIONS -->
            <title-card title="Survey options" no-padding help="subpanel-settings-survey-options">
                <v-container fluid class="pt-0 pl-5">

                    <v-row dense>

                        <v-col cols=4 lg="3">
                            <v-switch dense v-model="survey.enabled" label="Enable registration"></v-switch>
                        </v-col>

                        <v-col cols=4 lg="3">
                            <v-switch dense v-model="survey.show_progress" label="Show progress"></v-switch>
                        </v-col>

                        <v-col cols=4 lg="3">
                            <v-switch dense :disabled="!reCaptchaAvailable" v-model="survey.use_recaptcha"
                                      label="Use reCaptcha"></v-switch>
                        </v-col>

                        <v-col cols=4 lg="3">
                            <v-switch dense v-model="survey.auto_fill_region"
                                      label="Auto fill region details"></v-switch>
                        </v-col>

                    </v-row>
                </v-container>
            </title-card>

            <!-- QUESTIONS --->
            <title-card title="Questions" class="mt-2" help="subpanel-settings-survey-questions">

                <template v-slot:buttons>

                    <v-scale-transition>
                        <v-btn small @click="selectVariablesDialogModel = true"
                               color="primary">
                            Select questions
                        </v-btn>
                    </v-scale-transition>
                </template>

                <div class="d-flex">
                    <v-alert v-show="questions.length > 1 && showDragInfoBox"
                             class="body-2"
                             type="info" dismissible @input="removeDragInfoBox">
                        Your can drag and drop the questions in the desired order!
                    </v-alert>
                </div>

                <v-alert v-show="questions.length === 0" class="body-2 mb-0" type="info">
                    No questions found. Click on the "select questions" -button to add some.
                </v-alert>

                <div class="grid">

                    <div v-for="question in questions"
                         :key="question.id"
                         class="item"
                         :data-id="question.id">

                        <div class="item-content">

                            <question-box
                                :required="isQuestionRequired(question)"
                                :can-be-deleted="mandatoryQuestions && !mandatoryQuestions.includes(question.name)"
                                :locales="question.locales"
                                :required-locales="subPanel.locales"
                                :editable="true"
                                :variable="question"
                                @delete="deleteQuestion"
                            ></question-box>
                        </div>

                    </div>
                </div>

                <!-- PASSWORD FAKE QUESTION -->
                <div class="item-content">

                    <div class="item">
                        <question-box :can-be-deleted="false"
                                      :required="true"
                                      name="Create password"
                                      drag-handle-icon="mdi-lock"
                                      drag-handle-color="grey lighten-1"
                                      @delete="deleteQuestion"
                        ></question-box>
                    </div>
                </div>

                <!-- CAPTCHA FAKE QUESTION -->
                <v-scale-transition>
                    <div class="item-content" style="margin-top: 10px" v-show="survey.use_recaptcha">

                        <div class="item">
                            <question-box :can-be-deleted="false"
                                          :required="true"
                                          name="Google reCaptcha"
                                          drag-handle-icon="mdi-security"
                                          drag-handle-color="grey lighten-1"
                                          @delete="deleteQuestion"
                            ></question-box>
                        </div>
                    </div>
                </v-scale-transition>

            </title-card>

            <registration-links
                :sub-panel="subPanel"
                :hidden="!survey.enabled"
                help="subpanel-settings-registration-survey-links"
            ></registration-links>

        </v-card-text>

        <data-variable-picker v-model="selectVariablesDialogModel"
                              :data-variables="selectableDataVariables"
                              @close="selectVariablesDialogModel=false"
                              :selected="questions"
                              multiple
                              @change="variablesSelected">
        </data-variable-picker>

    </v-card>

</template>

<script>

import Json from 'vue-json-pretty';
import Store from '../../store';
import {mapState, mapMutations, mapActions} from 'vuex';
import activeSessionChecker from '../../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../../components/UI/MaterialHeader';
import DataVariablePicker from "../../../../components/UI/DataVariablePicker";
import Muuri from 'muuri';
import QuestionBox from "../QuestionBox";
import TitleCard from "../../../../components/UI/TitleCard";
import RegistrationLinks from "../RegistrationLinks";
import {registrationQuestionsMixin} from "../../../../components/mixins/registrationQuestionsMixin";

const vuexModule = 'SubPanels';

export default {

    mixins: [activeSessionChecker, registrationQuestionsMixin],

    components: {
        Json,
        MaterialHeader,
        DataVariablePicker,
        QuestionBox,
        TitleCard,
        RegistrationLinks
    },

    props: {
        subPanel: {
            type: Object,
            required: true,
        }
    },

    data() {
        return {
            minPermission: 'view registration surveys',
            selectVariablesDialogModel: false,
            questions: [],
            grid: null,
            survey: {
                enabled: false,
                show_progress: true,
                use_recaptcha: false,
                auto_fill_region: true,
                questions: [],
            },
        }
    },
    computed: {

        ...mapState(['dataVariables']),

        selectableDataVariables() {
            return this.dataVariables.filter(variable => variable.panelist_writable);
        },

        showDragInfoBox() {
            return localStorage.getItem('registrationSurveyDragInfoBox') === null;
        },

        reCaptchaAvailable() {
            return !!this.$store.state.PanelSettings.settings.integrations.find(integration => {
                return integration.name === 'recaptcha' && integration.enabled === true;
            });
        }
    },

    methods: {

        ...mapActions(['getDataVariables']),

        ...mapActions(vuexModule, [
            'loadRegistrationSurvey',
            'saveRegistrationSurvey'
        ]),

        ...mapMutations(vuexModule, []),

        variablesSelected(variables) {
            this.questions = [...variables];

            if (this.grid) this.grid.destroy();

            this.$nextTick(() => {
                this.createMuuriInstance();
                this.updateOrder();
            });

            this.emit();
        },

        createMuuriInstance() {

            this.grid = new Muuri('.grid', {
                dragAxis: 'y',
                dragEnabled: true,
                layout: {
                    fillGaps: true
                },
                dragHandle: '.drag-handle'
            });

            this.grid.on('dragReleaseEnd', items => {
                this.updateOrder()
            });
        },

        updateOrder() {
            this.survey.questions = this.grid.getItems()
                .map(item => item.getElement()
                    .getAttribute('data-id'));

            this.emit();
        },

        deleteQuestion(id) {

            const question = this.dataVariables.find(variable => {
                return variable.id === parseInt(id);
            });

            if (this.mandatoryQuestions.includes(question.name)) {
                Bus.$emit('flash-message', {
                    type: 'error',
                    message: question.name + ' is required and cannot be deleted'
                });
                return;
            }

            // Find element using data variable ID
            const element = this.grid.getItems(index)
                .find(item => item.getElement()
                    .getAttribute('data-id') === '' + id);

            // Remove element from the grid
            this.grid.remove([element]);

            // Remove data variables from selected
            const index = this.questions.findIndex(question => question.id === id);
            this.questions.splice(index, 1);

            // After dom update update order
            this.$nextTick(() => {
                this.grid.refreshItems().layout();
            });

            this.updateOrder();
        },

        emit() {
            this.$emit('change', this.survey);
        },

        removeDragInfoBox() {
            localStorage.registrationSurveyDragInfoBox = JSON.stringify(true);
        },

        isQuestionRequired(question) {
            if (this.mandatoryQuestions.includes(question.name)) return true;
            if (!question.dynamic_profiling_validators) return false;
            return !!question.dynamic_profiling_validators.find(validator => validator.type === 'required');
        }
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }
    },

    mounted() {

        this.getDataVariables()
            .then(() => {

                this.loadRegistrationSurvey({id: this.subPanel.id})
                    .then(({data}) => {
                        this.survey = Object.assign({}, data.survey);

                        this.survey.questions.forEach(id => {
                            this.questions.push(this.dataVariables.find(variable => variable.id === id));
                        });

                        if (!this.reCaptchaAvailable) {
                            this.survey.use_recaptcha = false;
                        }

                        this.emit();

                        this.$nextTick(() => {
                            this.createMuuriInstance();
                        });
                    })
                    .catch(error => {

                        if (error.response.status === 404) {

                            this.mandatoryQuestions.forEach(name => {

                                const variable = this.dataVariables.find(variable => variable.name === name);

                                this.questions.push(variable);
                                this.survey.questions.push(variable.id);
                            });

                            this.emit();

                            this.$nextTick(() => {
                                this.createMuuriInstance();
                            });

                        } else {
                            Bus.$emit('flash-message', {
                                type: 'error',
                                message: 'Oops! We failed loading the registration survey!'
                            });
                        }
                    })
            });
    },
}

</script>

<style scoped>

.grid {
    position: relative;
    transition: opacity 0.8s linear 1s;
    height: 100%;
}

.item {
    display: block;
    position: absolute;
    z-index: 1;
    margin: 5px;
    height: 50px;
    width: calc(100% - 10px);
}

.item.muuri-item-hidden {
    z-index: 0;
}

.item.muuri-item-positioning {
    z-index: 2;
}

.item.muuri-item-releasing {
    z-index: 3;
}

.item.muuri-item-dragging {
    z-index: 4;
}

.item-content {
    position: relative;
    width: 100%;
    height: 50px;
    text-align: center;
}

.item.muuri-item-dragging .item-content {

}

.item.muuri-item-releasing .item-content {

}
</style>
